import { apiGet, apiUpload } from "../api-client"
import type { NMCasingRecord, NMCompletionRecord } from "./nm"

// Plan step types
export interface PlanStep {
  type: string
  formation?: string | null
  sacks: number | null
  top_ft: number | null
  bottom_ft: number | null
  cement_class?: string | null
  depth_mid_ft?: number | null
  details: Record<string, any>
  // Canonical shape is string[]; NM C-103 PlanSnapshots store a single string at
  // runtime, which consumers must coerce (see PlanDetails step render).
  regulatory_basis: string[] | null
  special_instructions: string | null
  // NM C-103 step schema (PlanSnapshot stores these instead of the TX field names above)
  step_type?: string
  depth_top_ft?: number | null
  depth_bottom_ft?: number | null
  description?: string | null
  formations_referenced?: string[]
  category?: string | null
}

// Well information
export interface WellInfo {
  api14: string
  state: string
  county: string
  operator_name: string
  field_name: string
  lease_name: string
  well_number: string
  lat: number
  lon: number
}

// Well geometry interfaces to match backend response
export interface CasingString {
  string: string
  size_in: number
  top_ft: number
  bottom_ft: number
  shoe_depth_ft: number
  casing_type?: string
  string_type?: string
  weight_ppf?: number
  hole_size_in?: number
  cement_top_ft?: number | null
}

export interface FormationTop {
  formation: string
  top_ft: number
  base_ft: number
}

export interface TubingString {
  size_in: number
  bottom_ft: number
}

export interface WellGeometry {
  casing_strings: CasingString[]
  formation_tops: FormationTop[]
  tubing: TubingString[]
  perforations: any[]
  liner: any[]
}

// Plan payload
export interface PlanPayload {
  api: string
  field: string
  county: string
  district: string
  jurisdiction: string
  steps: PlanStep[]
  rrc_export: any[]
  violations: any[]
  materials_totals: {
    total_bbl: number
    total_sacks: number
  }
  formations_targeted: string[]
  [key: string]: any
}

// Full plan response
export interface PlanResponse {
  id: number
  plan_id: string
  kind: string
  status: string
  visibility: string
  tenant_id: string
  well: WellInfo
  kernel_version: string
  policy_id: string
  overlay_id: string
  extraction_meta: {
    files: string[]
    source: string
    status: string
    output_dir: string
  }
  created_at: string
  payload: PlanPayload
  well_geometry?: WellGeometry // Added optional well_geometry field
}

// W3A Generation Request
export interface W3AGenerationRequest {
  api10: string // Changed from 'api' to 'api10' to match backend spec
  plugs_mode?: "combined" | "isolated" | "both"
  input_mode?: "extractions" | "user_files" | "hybrid" // Updated values to match backend spec
  merge_threshold_ft?: number // Changed from 'combine_depth_ft' to 'merge_threshold_ft'
  sack_limit_no_tag?: number
  sack_limit_with_tag?: number
  confirm_fact_updates?: boolean
  allow_precision_upgrades_only?: boolean
  use_gau_override_if_invalid?: boolean
  files?: {
    gau_file?: File // Added _file suffix to match backend spec
    w2_file?: File
    w15_file?: File
    schematic_file?: File
    formation_tops_file?: File
  }
}

// W3A Generation Response
export interface W3AGenerationResponse {
  plan_id: string
  api: string
  status: string
  message: string
  variants?: string[]
}

/**
 * Fetch a plan by plan_id
 * The plan_id should be in the format: {api14}:{variant} (e.g., "4230132998:isolated" or "4230132998:combined")
 */
export async function fetchPlan(planId: string, bustCache = false): Promise<PlanResponse> {
  console.log("[v0] Fetching plan:", planId, bustCache ? "(cache-busted)" : "")

  const url = bustCache ? `/api/plans/${planId}/?_t=${Date.now()}` : `/api/plans/${planId}/`

  const response = await apiGet<PlanResponse>(url, bustCache ? { cache: "no-store" } : undefined)

  console.log("[v0] Plan response:", response)

  return response
}

/**
 * Helper function to get the correct plan_id for a well
 * Fetches the well data and extracts the plan_id from metadata
 * Falls back to {apiNumber}:combined if metadata is not available
 */
export async function getPlanIdForWell(apiNumber: string): Promise<string> {
  try {
    // Import fetchWell dynamically to avoid circular dependency
    const { fetchWell } = await import("./wells")
    const well = await fetchWell(apiNumber)

    // Extract plan_id from metadata if available
    if (well.tenant_interaction?.metadata?.plan_id) {
      console.log("[v0] Found plan_id in well metadata:", well.tenant_interaction.metadata.plan_id)
      return well.tenant_interaction.metadata.plan_id
    }

    // Fallback to :combined
    console.log("[v0] No plan_id in metadata, using default :combined")
    return `${apiNumber}:combined`
  } catch (error) {
    console.warn("[v0] Failed to fetch well for plan_id, using default :combined:", error)
    return `${apiNumber}:combined`
  }
}

/**
 * Fetch plan history/snapshots for a well
 */
export async function fetchPlanHistory(apiNumber: string): Promise<any> {
  const response = await apiGet(`/api/plans/${apiNumber}/history`)
  return response
}

/**
 * Generate a W3A plugging plan from an API number
 */
export async function generateW3APlan(request: W3AGenerationRequest): Promise<W3AGenerationResponse> {
  console.log("[v0] Generating W3A plan:", request)

  const formData = new FormData()
  formData.append("api10", request.api10)

  if (request.plugs_mode) {
    formData.append("plugs_mode", request.plugs_mode)
  }

  if (request.input_mode) {
    formData.append("input_mode", request.input_mode)
  }

  if (request.merge_threshold_ft !== undefined) {
    formData.append("merge_threshold_ft", request.merge_threshold_ft.toString())
  }

  if (request.sack_limit_no_tag !== undefined) {
    formData.append("sack_limit_no_tag", request.sack_limit_no_tag.toString())
  }

  if (request.sack_limit_with_tag !== undefined) {
    formData.append("sack_limit_with_tag", request.sack_limit_with_tag.toString())
  }

  if (request.confirm_fact_updates !== undefined) {
    formData.append("confirm_fact_updates", request.confirm_fact_updates.toString())
  }

  if (request.allow_precision_upgrades_only !== undefined) {
    formData.append("allow_precision_upgrades_only", request.allow_precision_upgrades_only.toString())
  }

  if (request.use_gau_override_if_invalid !== undefined) {
    formData.append("use_gau_override_if_invalid", request.use_gau_override_if_invalid.toString())
  }

  if (request.files) {
    if (request.files.gau_file) formData.append("gau_file", request.files.gau_file)
    if (request.files.w2_file) formData.append("w2_file", request.files.w2_file)
    if (request.files.w15_file) formData.append("w15_file", request.files.w15_file)
    if (request.files.schematic_file) formData.append("schematic_file", request.files.schematic_file)
    if (request.files.formation_tops_file) formData.append("formation_tops_file", request.files.formation_tops_file)
  }

  const response = await apiUpload<W3AGenerationResponse>("/api/plans/w3a/from-api", formData, { timeout: 600000 })

  console.log("[v0] W3A generation response:", response)

  return response
}

export async function checkPlanExists(apiNumber: string, plugsMode: string): Promise<PlanResponse | null> {
  // Normalize to 10-digit API to match backend storage format
  const digits = apiNumber.replace(/\D/g, "")
  const api10 = digits.length === 14 ? digits.slice(0, 10) : digits
  if (!api10 || api10.length < 8) return null

  const planId = plugsMode === "both" ? `${api10}:combined` : `${api10}:${plugsMode}`
  console.log("[v0] Checking for existing plan:", planId)

  try {
    const plan = await fetchPlan(planId)
    console.log("[v0] Existing plan found:", planId)
    return plan
  } catch (error) {
    console.log("[v0] No existing plan at:", planId)
    return null
  }
}

// ============================================================
// SEGMENTED W3A FLOW - Multi-stage with user verification
// ============================================================

export interface W3AInitialRequest {
  api10: string
  jurisdiction?: "TX" | "NM"
  input_mode?: "extractions" | "user_files" | "hybrid" | "manual"
  workspace_id?: string | null
  gau_file?: File
  w2_file?: File
  w15_file?: File
  schematic_file?: File
  formation_tops_file?: File
  district?: string
  county?: string
  field_name?: string
  lease?: string
  well_no?: string
  total_depth?: number
  has_uqw?: boolean
  uqw_base_depth?: number
}

// NM Well Data (for NM jurisdiction wells)
export interface NMWellDataForPlan {
  api10: string
  api14: string
  well_name: string
  operator_name: string
  operator_number: string
  status: string
  well_type: string
  direction: string
  surface_location: string
  latitude: number | null
  longitude: number | null
  elevation_ft: number | null
  proposed_depth_ft: number | null
  tvd_ft: number | null
  formation: string
  spud_date: string | null
  completion_date: string | null
  casing_records?: NMCasingRecord[]
  completions?: NMCompletionRecord[]
}

// NM Document (for NM jurisdiction wells)
export interface NMDocumentForPlan {
  filename: string
  url: string
  file_size: string | null
  date: string | null
  doc_type: string | null
}

export interface W3AInitialResponse {
  temp_plan_id: string
  combined_pdf_url: string
  combined_pdf_path: string
  source_files: Array<{
    index: number
    filename: string
    path: string
    page_count: number
    page_range: [number, number]
  }>
  api: string
  page_count: number
  file_size: number
  ttl_expires_at: string
  // NM-specific fields
  jurisdiction?: "TX" | "NM"
  requires_manual_entry?: boolean
  well_data?: NMWellDataForPlan
  nm_documents?: NMDocumentForPlan[]
}

export interface DocumentOverride {
  source_index: number
  action: "accept" | "reject" | "replace"
  replacement_file?: File
  reason?: string
}

export interface W3AConfirmDocsRequest {
  document_overrides?: DocumentOverride[]
  additional_uploads?: File[]
}

export interface ExtractionHumanReadableSummary {
  casing_strings?: number
  missing_fields?: string[]
  requires_manual_entry?: boolean
  [key: string]: any
}

export interface ExtractionResult {
  extracted_document_id: number
  document_type: string
  filename: string
  extraction_status: string
  errors?: string[]
  json_data: Record<string, any>
  human_readable_summary: ExtractionHumanReadableSummary
}

export interface W3AExtractionsResponse {
  temp_plan_id: string
  extractions: ExtractionResult[]
  extraction_count: number
  // NM-specific fields
  jurisdiction?: "TX" | "NM"
  requires_manual_entry?: boolean
}

export interface ExtractionEdit {
  extracted_document_id: number
  field_path: string
  field_label?: string
  original_value: any
  edited_value: any
  reason?: string
}

export interface W3AConfirmExtractionsRequest {
  edits?: ExtractionEdit[]
}

export interface GeometryField {
  field_id: string
  field_label: string
  value: any
  unit?: string
  source: string
  editable: boolean
}

export interface W3AGeometryResponse {
  temp_plan_id: string
  api: string
  casing_strings: GeometryField[]
  formation_tops: GeometryField[]
  perforations: GeometryField[]
  mechanical_barriers: GeometryField[]
  uqw_data?: Record<string, any>
  kop_data?: Record<string, any>
  // NM-specific fields
  jurisdiction?: "TX" | "NM"
  requires_manual_entry?: boolean
}

export interface GeometryEdit {
  field_id: string
  field_label?: string
  original_value: any
  edited_value: any
  reason?: string
}

export interface W3AConfirmGeometryRequest {
  edits?: GeometryEdit[]
  plugs_mode?: "combined" | "isolated" | "both"
  /**
   * Max distance (ft) between adjacent plugs for them to be combined.
   * Omit to use the server default (300 ft). Do NOT send 0 unless you mean
   * "never combine" — 0 is a legal value with that meaning.
   */
  max_gap_ft?: number
  /**
   * Max combined plug length (ft). The server clamps this to the
   * jurisdiction ceiling (TX 1,250 ft / NM 1,000 ft); omit to use the
   * full jurisdiction cap.
   */
  max_plug_length_ft?: number
}

export interface ApplyEditsRequest {
  edit_ids: string[]
  apply_reason?: string
}

/**
 * Stage 1: Initialize W3A flow (document sourcing)
 */
export async function w3aInitial(request: W3AInitialRequest): Promise<W3AInitialResponse> {
  console.log("[w3a-segmented] Stage 1: Initializing with document sourcing")

  const formData = new FormData()
  formData.append("api10", request.api10)

  if (request.jurisdiction) {
    formData.append("jurisdiction", request.jurisdiction)
  }

  if (request.input_mode) {
    formData.append("input_mode", request.input_mode)
  }

  if (request.workspace_id) {
    formData.append("workspace_id", request.workspace_id)
  }

  if (request.gau_file) formData.append("gau_file", request.gau_file)
  if (request.w2_file) formData.append("w2_file", request.w2_file)
  if (request.w15_file) formData.append("w15_file", request.w15_file)
  if (request.schematic_file) formData.append("schematic_file", request.schematic_file)
  if (request.formation_tops_file) formData.append("formation_tops_file", request.formation_tops_file)

  if (request.district) formData.append("district", request.district)
  if (request.county) formData.append("county", request.county)
  if (request.field_name) formData.append("field_name", request.field_name)
  if (request.lease) formData.append("lease", request.lease)
  if (request.well_no) formData.append("well_no", request.well_no)
  if (request.total_depth) formData.append("total_depth", String(request.total_depth))
  if (request.has_uqw) formData.append("has_uqw", "true")
  if (request.uqw_base_depth) formData.append("uqw_base_depth", String(request.uqw_base_depth))

  const response = await apiUpload<W3AInitialResponse>("/api/w3a/initial", formData)
  console.log("[w3a-segmented] Initial response:", response)

  return response
}

/**
 * Stage 2: Confirm documents and trigger extraction
 */
export async function w3aConfirmDocs(
  tempPlanId: string,
  request: W3AConfirmDocsRequest
): Promise<W3AExtractionsResponse> {
  console.log("[w3a-segmented] Stage 2: Confirming documents")
  
  const formData = new FormData()
  
  if (request.document_overrides) {
    formData.append("document_overrides", JSON.stringify(request.document_overrides))
  }
  
  if (request.additional_uploads) {
    request.additional_uploads.forEach((file, index) => {
      formData.append(`additional_uploads[${index}]`, file)
    })
  }
  
  const response = await apiUpload<W3AExtractionsResponse>(
    `/api/w3a/${tempPlanId}/confirm-docs`,
    formData,
    { timeout: 300000 } // 5 min for extraction
  )
  
  console.log("[w3a-segmented] Extractions response:", response)
  return response
}

/**
 * Stage 3: Confirm/edit extractions
 */
export async function w3aConfirmExtractions(
  tempPlanId: string,
  request: W3AConfirmExtractionsRequest
): Promise<{ detail: string; edits_saved: number }> {
  console.log("[w3a-segmented] Stage 3: Confirming extractions")
  
  const { apiPost } = await import("../api-client")
  const response = await apiPost<{ detail: string; edits_saved: number }>(
    `/api/w3a/${tempPlanId}/extractions`,
    request
  )
  
  console.log("[w3a-segmented] Extractions confirmed:", response)
  return response
}

/**
 * Stage 4: Get derived geometry
 */
export async function w3aGetGeometry(tempPlanId: string): Promise<W3AGeometryResponse> {
  console.log("[w3a-segmented] Stage 4: Getting geometry")
  
  const response = await apiGet<W3AGeometryResponse>(`/api/w3a/${tempPlanId}/geometry`)
  console.log("[w3a-segmented] Geometry response:", response)
  
  return response
}

/**
 * Stage 5: Confirm/edit geometry and trigger plan build
 */
export interface W3AConfirmGeometryResponse {
  detail: string
  edits_saved: number
  temp_plan_id: string
  formation_tops_count: number
  formation_tops: Array<{
    name: string
    depth_ft: number | null
    source: string
  }>
  mechanical_barriers_count: number
  mechanical_barriers: Array<{
    type: string
    depth_ft: number | null
    description: string
    source: string
  }>
  plan_built: boolean
  plan_id?: string
  plan_build_error?: string
}

export async function w3aConfirmGeometry(
  tempPlanId: string,
  request: W3AConfirmGeometryRequest
): Promise<W3AConfirmGeometryResponse> {
  console.log("[w3a-segmented] Stage 5: Confirming geometry")
  
  const { apiPost } = await import("../api-client")
  const response = await apiPost<W3AConfirmGeometryResponse>(
    `/api/w3a/${tempPlanId}/confirm-geometry`,
    request
  )
  
  console.log("[w3a-segmented] Geometry confirmed:", response)
  console.log("[w3a-segmented] Formation tops saved:", response.formation_tops)
  return response
}

/**
 * Stage 6: Apply staged edits to WellRegistry
 */
export async function w3aApplyEdits(
  planId: string,
  request: ApplyEditsRequest
): Promise<{ detail: string; applied_count: number }> {
  console.log("[w3a-segmented] Stage 6: Applying edits to WellRegistry")

  const { apiPost } = await import("../api-client")
  const response = await apiPost<{ detail: string; applied_count: number }>(
    `/api/w3a/${planId}/apply-edits`,
    request
  )

  console.log("[w3a-segmented] Edits applied:", response)
  return response
}

// ============================================================
// PLAN APPROVAL WORKFLOW
// ============================================================

/**
 * Plan approval status enum
 */
export type PlanApprovalStatus =
  | "draft"
  | "internal_review"
  | "engineer_approved"
  | "filed"
  | "under_agency_review"
  | "agency_approved"
  | "agency_rejected"
  | "revision_requested"

/**
 * Status change history entry
 */
export interface StatusChange {
  status: PlanApprovalStatus
  changed_at: string
  changed_by: string
  change_type: "+" | "~" | "-"  // create, update, delete
}

/**
 * Plan status response
 */
export interface PlanStatusResponse {
  plan_id: string
  current_status: PlanApprovalStatus
  kind: string
  visibility: "private" | "public"
  tenant_id: string
  created_at: string
  history: StatusChange[]
}

/**
 * Plan status update response
 */
export interface PlanStatusUpdateResponse {
  success: boolean
  plan_id: string
  status: PlanApprovalStatus
  message: string
}

/**
 * Get plan approval status
 * GET /api/plans/{plan_id}/status/
 */
export async function getPlanStatus(planId: string): Promise<PlanStatusResponse> {
  console.log("[plan-approval] Getting status for plan:", planId)

  const response = await apiGet<PlanStatusResponse>(`/api/plans/${planId}/status/`)

  console.log("[plan-approval] Status response:", response)
  return response
}

/**
 * Move plan to internal review
 * POST /api/plans/{plan_id}/status/modify/
 */
export async function movePlanToReview(planId: string): Promise<PlanStatusUpdateResponse> {
  console.log("[plan-approval] Moving plan to internal review:", planId)

  const { apiPost } = await import("../api-client")
  const response = await apiPost<PlanStatusUpdateResponse>(
    `/api/plans/${planId}/status/modify/`,
    {}
  )

  console.log("[plan-approval] Move to review response:", response)
  return response
}

/**
 * Approve plan (engineer approval)
 * POST /api/plans/{plan_id}/status/approve/
 */
export async function approvePlan(planId: string): Promise<PlanStatusUpdateResponse> {
  console.log("[plan-approval] Approving plan:", planId)

  const { apiPost } = await import("../api-client")
  const response = await apiPost<PlanStatusUpdateResponse>(
    `/api/plans/${planId}/status/approve/`,
    {}
  )

  console.log("[plan-approval] Approval response:", response)
  return response
}

/**
 * File plan with agency
 * POST /api/plans/{plan_id}/status/file/
 */
export async function filePlan(planId: string): Promise<PlanStatusUpdateResponse> {
  console.log("[plan-approval] Filing plan with agency:", planId)

  const { apiPost } = await import("../api-client")
  const response = await apiPost<PlanStatusUpdateResponse>(
    `/api/plans/${planId}/status/file/`,
    {}
  )

  console.log("[plan-approval] Filing response:", response)
  return response
}


